import { useEffect, useRef, useState } from 'react';
import { businessConfig } from '../../../config/businessConfig';
import { galleryItems } from '../../../data/gallery';

function GalleryLightbox({ activeIndex, onClose, onChange }) {
  const closeButtonRef = useRef(null);
  const activeItem = galleryItems[activeIndex];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onChange(-1);
      if (event.key === 'ArrowRight') onChange(1);

      if (event.key === 'Tab') {
        const controls = Array.from(
          event.currentTarget?.document?.querySelectorAll('.gallery-lightbox button') ?? [],
        );
        const firstControl = controls.at(0);
        const lastControl = controls.at(-1);

        if (event.shiftKey && document.activeElement === firstControl) {
          event.preventDefault();
          lastControl?.focus();
        } else if (!event.shiftKey && document.activeElement === lastControl) {
          event.preventDefault();
          firstControl?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onChange, onClose]);

  if (!activeItem) return null;

  return (
    <div
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Trabajo ${activeIndex + 1} de ${galleryItems.length}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button
        ref={closeButtonRef}
        className="gallery-lightbox__close"
        type="button"
        onClick={onClose}
        aria-label="Cerrar imagen ampliada"
      >
        <span aria-hidden="true">×</span>
      </button>

      {galleryItems.length > 1 && (
        <button
          className="gallery-lightbox__nav gallery-lightbox__nav--previous"
          type="button"
          onClick={() => onChange(-1)}
          aria-label="Ver trabajo anterior"
        >
          <span aria-hidden="true">←</span>
        </button>
      )}

      <figure className="gallery-lightbox__content">
        <img src={activeItem.src} alt={activeItem.alt} />
        <figcaption>
          <small>{String(activeIndex + 1).padStart(2, '0')} / {String(galleryItems.length).padStart(2, '0')}</small>
        </figcaption>
      </figure>

      {galleryItems.length > 1 && (
        <button
          className="gallery-lightbox__nav gallery-lightbox__nav--next"
          type="button"
          onClick={() => onChange(1)}
          aria-label="Ver trabajo siguiente"
        >
          <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
}

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(null);
  const lastTriggerRef = useRef(null);

  const closeLightbox = () => {
    setActiveIndex(null);
    requestAnimationFrame(() => lastTriggerRef.current?.focus());
  };
  const changeLightboxItem = (direction) => {
    setActiveIndex((currentIndex) => (
      (currentIndex + direction + galleryItems.length) % galleryItems.length
    ));
  };

  return (
    <section className="section gallery" id="galeria">
      <div className="container">
        <div className="section-heading section-heading--center">
          <p className="eyebrow">Trabajos reales</p>
          <h2 className="section-title">Cada detalle cuenta una historia.</h2>
          <p>Una selección de diseños realizados en Mujer Virtuosa, creados con dedicación para cada clienta.</p>
        </div>

        {galleryItems.length > 0 ? (
          <div className="gallery__grid">
            {galleryItems.map((item, index) => (
              <figure className={`gallery-card gallery-card--${item.layout}`} key={item.id}>
                <button
                  className="gallery-card__trigger"
                  type="button"
                  onClick={(event) => {
                    lastTriggerRef.current = event.currentTarget;
                    setActiveIndex(index);
                  }}
                  aria-label={`Ampliar ${item.alt}`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading={index < 2 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined}
                  />
                  <span className="gallery-card__overlay" aria-hidden="true">
                    <small>{String(index + 1).padStart(2, '0')}</small>
                    <span>Ver detalle</span>
                  </span>
                </button>
              </figure>
            ))}
          </div>
        ) : (
          <div className="gallery__empty">
            <span aria-hidden="true">✦</span>
            <p>La selección de trabajos reales está siendo preparada.</p>
          </div>
        )}

        <a className="button button--line gallery__instagram" href={businessConfig.instagramUrl} target="_blank" rel="noreferrer">
          Ver más en Instagram <span aria-hidden="true">↗</span>
        </a>
      </div>

      {activeIndex !== null && (
        <GalleryLightbox
          activeIndex={activeIndex}
          onClose={closeLightbox}
          onChange={changeLightboxItem}
        />
      )}
    </section>
  );
}
